import { useEffect, useState } from 'react';
import { supabase, supabaseAnonKey, supabaseUrl } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { accountSaveFailure, connectAccountPersistence, disconnectAccountPersistence, remoteAccountDocument, saveAccountDocument } from '@/lib/accountPersistence';

export default function AccountDataSync({ children }: { children: React.ReactNode }) {
  const { user, signOut } = useAuth();
  const [ready, setReady] = useState(!user);
  const [message, setMessage] = useState('');
  useEffect(() => {
    let active = true;
    const changed = () => { if (active) setMessage(accountSaveFailure()); };
    window.addEventListener('lavyn-account-save', changed);
    if (!user) { disconnectAccountPersistence(); setReady(true); }
    else if (import.meta.env.VITE_LAVYN_ACCOUNT_DATA_SYNC !== 'true') {
      disconnectAccountPersistence();
      setMessage('Account cloud saving is not enabled yet. Your existing device copies are preserved and have not been uploaded.');
      setReady(true);
    } else {
      setReady(false);
      const owner = user.id;
      // auth.getSession is outside Supabase's onAuthStateChange callback.
      const request = async (query: string, options: RequestInit = {}) => {
        const { data, error } = await supabase.auth.getSession();
        if (!active || error || data.session?.user.id !== owner) throw new Error('Account changed.');
        const response = await fetch(`${supabaseUrl}/rest/v1/lavyn_account_documents?user_id=eq.${encodeURIComponent(owner)}&${query}`, {
          ...options, headers: { apikey: supabaseAnonKey,
            Authorization: `Bearer ${data.session.access_token}`, 'Content-Type': 'application/json',
            Prefer: 'return=representation', ...options.headers }, signal: AbortSignal.timeout(12000),
        });
        if (!response.ok) throw new Error('Account storage unavailable.');
        return response.json();
      };
      void connectAccountPersistence(owner, {
        async list() {
          const rows = [];
          for (let offset = 0; ; offset += 100) {
            const page = await request(`select=document_id,payload,revision&order=document_id&limit=100&offset=${offset}`);
            rows.push(...page);
            if (page.length < 100) return rows;
          }
        },
        async write(id, payload, revision) {
          const rows = await request(`document_id=eq.${encodeURIComponent(id)}${revision === null ? '' : `&revision=eq.${revision}`}`, {
            method: revision === null ? 'POST' : 'PATCH',
            body: JSON.stringify(revision === null ? { user_id: owner, document_id: id, payload, revision: 1 }
              : { payload, revision: revision + 1 }),
          });
          if (rows.length !== 1) throw new Error('Account record changed. Reload before saving.');
          return rows[0];
        },
      }).then(async connected => {
        if (!active) return;
        if (connected) {
          // Import only the currently authenticated account's explicitly scoped
          // records. Never scan/import shared legacy keys or another account.
          const prefix = `lavyn_private_v2:${encodeURIComponent(owner)}:`;
          const keys = Array.from({ length: localStorage.length }, (_, i) => localStorage.key(i))
            .filter((key): key is string => Boolean(key?.startsWith(prefix) && !key.slice(prefix.length).startsWith('flow:')));
          const chatKey = `lavyn_chat_conversations:${owner}`;
          if (localStorage.getItem(chatKey)) keys.push(chatKey);
          for (const key of keys) {
            const id = key === chatKey ? 'chats' : key.slice(prefix.length);
            if (!remoteAccountDocument(owner, id)) {
              try { await saveAccountDocument(owner, id, JSON.parse(localStorage.getItem(key)!)); }
              catch { break; }
            }
          }
        }
      }).catch(() => {
        if (active) setMessage('Account saving could not be initialized. Your existing device data has not been deleted.');
      }).finally(() => { if (active) setReady(true); });
    }
    return () => { active = false; disconnectAccountPersistence(); window.removeEventListener('lavyn-account-save', changed); };
  }, [user?.id]);
  if (!ready) return <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-neutral-950 text-neutral-100"><p>Opening your saved learning space…</p><button onClick={() => void signOut()}>Sign out</button></div>;
  return <>{message && <div role="alert" className="bg-neutral-800 text-white text-sm p-3">{message}</div>}{children}</>;
}
